import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  InputSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  signal,
  untracked,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, {Region, RegionParams} from "wavesurfer.js/plugins/regions";
import TimelinePlugin from "wavesurfer.js/plugins/timeline";
import Hover from "wavesurfer.js/plugins/hover";
import ZoomPlugin from "wavesurfer.js/plugins/zoom";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {TranslatePipe} from "@ngx-translate/core";
import {MatTooltip} from "@angular/material/tooltip";
import {MatProgressBar} from "@angular/material/progress-bar";

// Longer files are streamed without a waveform: the duration limit guards the memory needed to decode the peaks
const WAVEFORM_MAX_DURATION_SECONDS = 4500;
// The size limit guards the download, as the whole file (also video) is held in memory to build the waveform
const WAVEFORM_MAX_FILE_BYTES = 500 * 1024 * 1024;
const WAVEFORM_TOO_LARGE_ERROR = 'WaveformTooLargeError';
// Peaks do not need more resolution, and a low sample rate keeps the decoded audio small
const WAVEFORM_SAMPLE_RATE = 8000;

interface WaveformData {
  channelData: Float32Array[];
  duration: number;
}

@Component({
  selector: 'app-audio-waveform-player',
  imports: [
    MatIcon,
    MatIconButton,
    TranslatePipe,
    MatTooltip,
    MatProgressBar,
  ],
  templateUrl: './audio-waveform-player.component.html',
  styleUrls: ['./audio-waveform-player.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AudioWaveformPlayerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('waveformEl') waveformEl!: ElementRef<HTMLDivElement>
  @ViewChild('mediaEl') mediaEl!: ElementRef<HTMLVideoElement>

  playbackUrl: InputSignal<string> = input();
  regions: InputSignal<RegionParams[]> = input([]);
  draftRegion: InputSignal<RegionParams | null> = input(null);
  activeRegionId: InputSignal<string | null> = input<string | null>(null);

  compact: InputSignal<boolean> = input(false);
  controlsOnTop: InputSignal<boolean> = input(false);
  enableTimeline: InputSignal<boolean> = input(true);
  enableHoverCursor: InputSignal<boolean> = input(true);
  enableRegionEdit: InputSignal<boolean> = input(false);
  showVideo: InputSignal<boolean> = input(false);
  // When set, play/pause only plays the active region, stopping at its end
  limitPlaybackToActiveRegion: InputSignal<boolean> = input(false);
  disabled: InputSignal<boolean> = input(false);

  playing: OutputEmitterRef<boolean> = output<boolean>();
  cursorMoved: OutputEmitterRef<number> = output<number>();
  // Ids of the non-draft regions the cursor is inside; several when regions overlap
  regionsAtCursor: OutputEmitterRef<string[]> = output<string[]>();
  // The user moved or started playback with the player itself, not through code
  playerControlUsed: OutputEmitterRef<void> = output<void>();
  regionUpdated: OutputEmitterRef<Region | null> = output<Region | null>();
  // Emits the playback url whose load failed
  loadFailed: OutputEmitterRef<string> = output<string>();

  isLoaded = signal(false);
  loadError = signal(false);
  // Download progress in percent; stays 0 when the server sends no Content-Length
  loadProgress = signal(0);
  // The file streams as soon as it is loaded; the waveform is built in the background
  waveformLoading = signal(false);
  waveformUnavailable = signal(false);
  isLoading = computed(() => !!this.playbackUrl() && !this.isLoaded() && !this.loadError());
  isPlaying = signal(false);
  zoomLevel = signal(0);
  currentTime = signal(0);
  duration = signal(0);

  playbackControlButtons = computed(() => {
    const now = this.currentTime();
    const loaded = this.isLoaded() && !this.disabled();
    const duration = this.duration();
    const markers = this.getMarkerStarts(false);
    const hasPrevMarker = this.hasPrevMarker(now, markers);
    const hasNextMarker = this.hasNextMarker(now, markers);

    return [
      {
        icon: 'first_page',
        tooltip: 'TRANS__AUDIO_PLAYER__SKIP_START_BUTTON_TOOLTIP',
        action: () => this.byUser(() => this.goToStart()),
        hidden: this.compact(),
        disabled: !loaded || now <= 0,
      },
      {
        icon: 'skip_previous',
        tooltip: 'TRANS__AUDIO_PLAYER__PREV_MARKER_BUTTON_TOOLTIP',
        action: () => this.byUser(() => this.goToPrevMarker()),
        hidden: this.compact(),
        disabled: !loaded || !hasPrevMarker,
      },
      {
        icon: 'replay',
        tooltip: 'TRANS__AUDIO_PLAYER__SKIP_BACK_BUTTON_TOOLTIP',
        action: () => this.byUser(() => this.skipBackward()),
        hidden: this.compact(),
        disabled: !loaded || now <= 0,
      },
      {
        icon: this.isPlaying() ? 'pause' : 'play_arrow',
        tooltip: 'TRANS__AUDIO_PLAYER__PLAY_TOGGLE_BUTTON_TOOLTIP',
        primary: true,
        action: () => this.togglePlayByUser(),
        disabled: !loaded,
      },
      {
        icon: 'forward_media',
        tooltip: 'TRANS__AUDIO_PLAYER__SKIP_FORWARD_BUTTON_TOOLTIP',
        action: () => this.byUser(() => this.skipForward()),
        hidden: this.compact(),
        disabled: !loaded || now >= duration,
      },
      {
        icon: 'skip_next',
        tooltip: 'TRANS__AUDIO_PLAYER__NEXT_MARKER_BUTTON_TOOLTIP',
        action: () => this.byUser(() => this.goToNextMarker()),
        hidden: this.compact(),
        disabled: !loaded || !hasNextMarker,
      },
      {
        icon: 'last_page',
        tooltip: 'TRANS__AUDIO_PLAYER__SKIP_END_BUTTON_TOOLTIP',
        action: () => this.byUser(() => this.goToEnd()),
        hidden: this.compact(),
        disabled: !loaded || now >= duration,
      },
    ];
  });

  canvasControlButtons = computed(() => [
    {
      icon: 'zoom_in',
      primary: false,
      disabled: this.disabled() || !this.isLoaded() || this.zoomLevel() === this.maxZoom,
      action: () => this.zoomIn(),
    },
    {
      icon: 'zoom_out',
      primary: false,
      disabled: this.disabled() || !this.isLoaded() || this.zoomLevel() === 0,
      action: () => this.zoomOut(),
    },
  ]);

  private ws!: WaveSurfer;
  private regionsPlugin!: RegionsPlugin;
  private wsReady = signal(false);
  private maxZoom = 1000; // Maximum allowed pixels-per-second value
  private readonly DRAFT_REGION_ID = 'draft-region';
  // Within this many seconds of a segment start, "previous" skips to the segment before
  private readonly PREV_MARKER_RESTART_THRESHOLD = 2;
  private syncingDraft = false;
  // Only the newest load may update the load state; wavesurfer resolves superseded loads too
  private loadId = 0;
  private mediaReady = false;
  private waveformAbort: AbortController | null = null;

  private cursorRegionId = signal<string | null>(null);
  private readonly idsAtCursor = new Set<string>();
  private highlightedRegionId: string | null = null;
  private pendingRegionId: string | null = null;

  constructor() {
    effect(() => {
      const url = this.playbackUrl();
      const ready = this.wsReady();
      if (ready && url) {
        untracked(() => this.load(url));
      }
    });

    effect(() => {
      const regionData = this.regions();
      const loaded = this.isLoaded();
      if (!loaded) return;
      this.setRegions(regionData);
    });

    effect(() => {
      this.highlightedRegionId = this.activeRegionId() ?? this.cursorRegionId();
      if (this.isLoaded()) this.applyRegionHighlight();
    });

    effect(() => {
      if (!this.isLoaded()) return;

      const draft = this.draftRegion();
      if (!draft) {
        this.clearDraftRegion();
        return;
      }

      this.setDraftRegion(draft);
    });
  }

  ngAfterViewInit(): void {
    this.ws = this.createWaveSurferInstance();
    this.ws.on('ready', (duration) => {
      this.duration.set(duration);
      if (this.mediaReady) {
        // Wavesurfer is ready again after the waveform replaced the placeholder; playback carries on
        this.restoreRegionStop();
        return;
      }
      this.mediaReady = true;
      if (this.pendingRegionId) {
        // A region was requested before the file loaded; keep until the regions are rendered
        this.ws.setTime(0);
      } else {
        this.goToStart();
      }
      if (this.enableRegionEdit()) {
        this.regionsPlugin.enableDragSelection({id: this.DRAFT_REGION_ID, drag: true, resize: true});
      }
    });
    this.ws.on('play', () => {
      this.isPlaying.set(true);
      this.playing.emit(true);
    });
    this.ws.on('pause', () => this.setNotPlaying());
    this.ws.on('error', (error) => {
      if (this.isLoading()) this.failLoad(this.playbackUrl(), error);
    });
    this.ws.on('loading', (percent: number) => this.loadProgress.set(percent));
    this.ws.on('zoom', (minPxPerSec: number) => this.zoomLevel.set(this.minPxPerSecToSlider(minPxPerSec)));
    this.ws.on('timeupdate', (currentTime: number) => {
      this.currentTime.set(currentTime);
    });
    this.ws.on('interaction', () => this.playerControlUsed.emit());
    this.ws.on('seeking', (currentTime: number) => {
      this.currentTime.set(currentTime);
      this.cursorMoved.emit(currentTime);
    })

    // Wavesurfer reports entering the new region before leaving the old one, so only leaving the current region clears it
    this.regionsPlugin?.on('region-in', (region) => {
      if (region.id === this.DRAFT_REGION_ID) return;
      this.cursorRegionId.set(region.id);
      this.idsAtCursor.add(region.id);
      this.emitRegionsAtCursor();
    });
    this.regionsPlugin?.on('region-out', (region) => {
      if (this.idsAtCursor.delete(region.id)) this.emitRegionsAtCursor();
      if (this.cursorRegionId() === region.id) this.cursorRegionId.set(null);
    });

    this.regionsPlugin.on('region-created', (created) => {
      if (created.id === this.DRAFT_REGION_ID && !this.syncingDraft) {
        // Clear any existing draft region (only one allowed at a time)
        this.clearDraftRegion();
      }
      this.regionUpdated.emit(created);
    });

    this.regionsPlugin.on('region-updated', (updated) => {
      this.regionUpdated.emit(updated);
    });

    this.wsReady.set(true);
  }

  ngOnDestroy(): void {
    this.loadId++;
    this.waveformAbort?.abort();
    this.ws?.destroy();
  }

  togglePlay(regionId?: string | null): void {
    if (!this.ws) return;
    if (this.ws.isPlaying()) {
      this.ws.pause();
    } else if (!regionId && this.limitPlaybackToActiveRegion() && this.activeRegionHasTimeRange()) {
      this.playWithinRegion(this.activeRegionId());
    } else {
      this.play(regionId);
    }
  }

  toggleRegionPlayback(regionId: string): void {
    if (!this.ws) return;
    if (this.ws.isPlaying()) {
      this.ws.pause();
    } else {
      this.playWithinRegion(regionId);
    }
  }

  play(regionId?: string | null): void {
    if (regionId) {
      const region = this.getRegion(regionId);
      if (region && this.hasTimeRange(region)) {
        region.play(true); // Play from region start and automatically stop at region end
      } else if (region) {
        this.stopAtRegion(region);
      }
    } else {
      this.ws?.playPause().then();
    }
  }

  goToStart(regionId?: string): void {
    this.pendingRegionId = null; // Cancel any pending region seek
    if (regionId) {
      const region = this.getRegion(regionId);
      this.ws?.setTime(region?.start ?? 0);
    } else {
      this.ws?.setTime(0);
    }
  }

  goToPrevMarker(): void {
    if (!this.ws) return;

    const now = this.ws.getCurrentTime();
    const markers = this.getMarkerStarts(false);
    const current = this.findPrevMarker(markers, now);
    const barelyStarted = current !== null && now - current < this.PREV_MARKER_RESTART_THRESHOLD;
    const target = barelyStarted ? this.findPrevMarker(markers, current) : current;

    this.ws.setTime(target ?? 0);
  }

  goToNextMarker(): void {
    if (!this.ws) return;

    const now = this.ws.getCurrentTime();
    const markers = this.getMarkerStarts(false);
    const next = this.findNextMarker(markers, now);

    this.ws.setTime(next ?? this.ws.getDuration());
  }

  goToEnd(regionId?: string): void {
    if (regionId) {
      const region = this.getRegion(regionId);
      this.ws?.setTime(region?.end ?? this.ws?.getDuration());
    } else {
      this.ws?.setTime(this.ws?.getDuration());
    }
  }

  goToRegion(regionId: string): void {
    const region = regionId ? this.getRegion(regionId) : null;
    if (!this.ws || !region) {
      // Player or regions may still be loading; retry once the regions are rendered
      this.pendingRegionId = regionId || null;
      return;
    }
    this.pendingRegionId = null;
    if (!this.hasTimeRange(region)) {
      this.stopAtRegion(region);
    } else if (this.ws.isPlaying()) {
      region.play(true);
    } else {
      this.ws.setTime(region.start);
    }
  }

  skipBackward(): void {
    if (!this.ws) return;
    const seconds = this.getSkipDuration();
    const target = this.ws.getCurrentTime() - seconds;
    this.ws.setTime(Math.max(target, 0));
  }

  skipForward(): void {
    if (!this.ws) return;
    const seconds = this.getSkipDuration();
    const target = this.ws.getCurrentTime() + seconds;
    this.ws.setTime(Math.min(target, this.ws.getDuration()));
  }

  zoomIn(): void {
    this.setZoom(Math.min(this.maxZoom, this.zoomLevel() + 50));
  }

  zoomOut(): void {
    this.setZoom(Math.max(0, this.zoomLevel() - 50));
  }

  onZoomChange(event: Event): void {
    const value = +(event.target as HTMLInputElement).value;
    this.setZoom(value);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) {
      return;
    }
    switch (event.code) {
      case 'Space':
        event.preventDefault();
        this.togglePlayByUser();
        break;
      case 'Home':
        event.preventDefault();
        this.byUser(() => this.goToStart());
        break;
      case 'End':
        event.preventDefault();
        this.byUser(() => this.goToEnd());
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (event.shiftKey) {
          this.byUser(() => this.goToPrevMarker());
        } else {
          this.byUser(() => this.skipBackward());
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (event.shiftKey) {
          this.byUser(() => this.goToNextMarker());
        } else {
          this.byUser(() => this.skipForward());
        }
        break;
      case 'Equal':
      case 'NumpadAdd':
        event.preventDefault();
        this.zoomIn();
        break;
      case 'Minus':
      case 'NumpadSubtract':
        event.preventDefault();
        this.zoomOut();
        break;
    }
  }

  formatTime(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    if (m > 0) {
      return `${m}:${String(s).padStart(2, '0')}`;
    }
    return `0:${String(s).padStart(2, '0')}`;
  }

  private togglePlayByUser(): void {
    if (this.ws && !this.ws.isPlaying()) this.playerControlUsed.emit();
    this.togglePlay();
  }

  private byUser(action: () => void): void {
    this.playerControlUsed.emit();
    action();
  }

  private emitRegionsAtCursor(): void {
    this.regionsAtCursor.emit([...this.idsAtCursor]);
  }

  private load(url: string): void {
    const loadId = ++this.loadId;
    if (this.idsAtCursor.size) {
      this.idsAtCursor.clear();
      this.emitRegionsAtCursor();
    }
    this.waveformAbort?.abort();
    this.mediaReady = false;
    // The media load algorithm pauses playback without a pause event
    this.setNotPlaying();
    this.currentTime.set(0);
    this.duration.set(0);
    this.zoomLevel.set(0);
    this.ws.setOptions({minPxPerSec: 0});
    this.loadProgress.set(0);
    this.isLoaded.set(false);
    this.loadError.set(false);
    this.waveformLoading.set(false);
    this.waveformUnavailable.set(false);
    this.clearWaveform();
    // Placeholder peaks make wavesurfer stream the url instead of downloading and decoding all of it first
    // Wavesurfer skips reloading the media element only when the url equals the resolved media.currentSrc
    const absoluteUrl = new URL(url, location.href).href;
    this.ws.load(absoluteUrl, [[0]]).then(
      () => {
        if (loadId !== this.loadId) return;
        this.isLoaded.set(true);
        this.loadWaveform(absoluteUrl, loadId);
      },
      (error) => {
        if (loadId === this.loadId) this.failLoad(url, error);
      });
  }

  private setNotPlaying(): void {
    if (!this.isPlaying()) return;
    this.isPlaying.set(false);
    this.playing.emit(false);
  }

  private failLoad(url: string, error: { name?: string } | undefined): void {
    if (this.loadError()) return; // Wavesurfer reports the same failure as both an event and a rejection
    // Log the error name only: the message includes the signed playback url
    console.error(`Failed to load media in the waveform player: ${error?.name ?? 'unknown error'}`);
    this.loadError.set(true);
    this.loadFailed.emit(url);
  }

  private async loadWaveform(url: string, loadId: number): Promise<void> {
    if (this.ws.getDuration() > WAVEFORM_MAX_DURATION_SECONDS) {
      this.waveformUnavailable.set(true);
      return;
    }
    const abort = new AbortController();
    this.waveformAbort = abort;
    this.waveformLoading.set(true);
    try {
      const waveform = await this.fetchWaveform(url, abort.signal, (percent) => {
        if (loadId === this.loadId) this.loadProgress.set(percent);
      });
      if (loadId !== this.loadId) return;
      await this.ws.load(url, waveform.channelData, waveform.duration);
      if (loadId === this.loadId) this.waveformLoading.set(false);
    } catch (error) {
      if (loadId === this.loadId) this.failWaveform(error);
    }
  }

  private failWaveform(error: { name?: string } | undefined): void {
    this.waveformLoading.set(false);
    if (error?.name === 'AbortError') return;
    if (error?.name === WAVEFORM_TOO_LARGE_ERROR) {
      this.waveformUnavailable.set(true);
      return;
    }
    // The file stays playable without a waveform; log the error name only as the message may include the signed url
    console.error(`Failed to build the waveform in the waveform player: ${error?.name ?? 'unknown error'}`);
    this.waveformUnavailable.set(true);
  }

  protected async fetchWaveform(url: string, signal: AbortSignal, onProgress: (percent: number) => void): Promise<WaveformData> {
    const response = await fetch(url, {signal});
    if (!response.ok) throw new Error(`Unexpected response status ${response.status}`);
    const total = this.getContentLength(response);
    if (total > WAVEFORM_MAX_FILE_BYTES) return this.rejectTooLarge(response);
    const buffer = await this.readWithProgress(response, total, onProgress);
    const context = new AudioContext({sampleRate: WAVEFORM_SAMPLE_RATE});
    try {
      const audio = await context.decodeAudioData(buffer);
      const channelData = Array.from({length: audio.numberOfChannels}, (_, channel) => audio.getChannelData(channel));
      return {channelData, duration: audio.duration};
    } finally {
      await context.close();
    }
  }

  // Zero when the server sends no usable Content-Length
  private getContentLength(response: Response): number {
    const total = Number(response.headers.get('Content-Length'));
    return Number.isFinite(total) && total > 0 ? total : 0;
  }

  private async rejectTooLarge(response: Response): Promise<never> {
    await response.body?.cancel().catch(() => undefined);
    const error = new Error('The file is too large to build a waveform from');
    error.name = WAVEFORM_TOO_LARGE_ERROR;
    throw error;
  }

  // Progress is only known when the server sends a Content-Length
  private async readWithProgress(response: Response, total: number, onProgress: (percent: number) => void): Promise<ArrayBuffer> {
    if (!response.body || !total) return response.arrayBuffer();

    const reader = response.body.getReader();
    // Preallocated so the file is held once; grown only if the body is longer than the declared length
    let bytes = new Uint8Array(total);
    let received = 0;
    for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
      if (received + chunk.value.length > bytes.length) bytes = this.grow(bytes, received + chunk.value.length);
      bytes.set(chunk.value, received);
      received += chunk.value.length;
      onProgress(Math.min(100, Math.round(received / total * 100)));
    }
    return received === bytes.length ? bytes.buffer : bytes.buffer.slice(0, received);
  }

  private grow(bytes: Uint8Array<ArrayBuffer>, minimumLength: number): Uint8Array<ArrayBuffer> {
    const grown = new Uint8Array(Math.max(bytes.length * 2, minimumLength));
    grown.set(bytes);
    return grown;
  }

  // Replacing the placeholder resets wavesurfer's stop at the end of a region, so it is set up again
  private restoreRegionStop(): void {
    const regionId = this.activeRegionId();
    const region = regionId ? this.getRegion(regionId) : null;
    if (!this.ws.isPlaying() || !this.limitPlaybackToActiveRegion() || !region || !this.hasTimeRange(region)) return;
    this.ws.play(this.ws.getCurrentTime(), region.end).then();
  }

  private createWaveSurferInstance(): WaveSurfer {
    const ws = WaveSurfer.create({
      container: this.waveformEl.nativeElement,
      media: this.mediaEl.nativeElement,
      height: this.compact() ? 140 : 160,
      waveColor: '#82a9cd',
      progressColor: '#1e639f',
      minPxPerSec: 0,
      fillParent: true,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      plugins: [
        ZoomPlugin.create({
          scale: 0.5,
          maxZoom: this.maxZoom,
        }),
      ],
    });

    this.regionsPlugin = RegionsPlugin.create();
    ws.registerPlugin(this.regionsPlugin)

    if (this.enableHoverCursor()) {
      ws.registerPlugin(Hover.create({
        lineColor: '#777',
        lineWidth: 2,
        labelBackground: '#555',
        labelColor: '#fff',
        labelSize: '11px',
        labelPreferLeft: false,
      }));
    }
    if (this.enableTimeline()) {
      ws.registerPlugin(TimelinePlugin.create());
    }

    return ws;
  }

  // Resumes from the cursor if it is inside the region, otherwise plays from the region start; stops at the region end
  private playWithinRegion(regionId: string, eps = 0.01): void {
    const region = this.getRegion(regionId);
    if (!region) {
      this.play();
      return;
    }
    if (!this.hasTimeRange(region)) {
      this.stopAtRegion(region);
      return;
    }
    const now = this.ws.getCurrentTime();
    const start = now >= region.start && now < region.end - eps ? now : region.start;
    this.ws.play(start, region.end).then();
  }

  // A segment of zero duration (e.g. 00:00:00-00:00:00) has no known time range, and is not playable
  private hasTimeRange(region: Region): boolean {
    return region.end > region.start;
  }

  // The play button plays from the cursor when the active segment has zero duration, as there is no range to limit to
  private activeRegionHasTimeRange(): boolean {
    const region = this.activeRegionId() ? this.getRegion(this.activeRegionId()) : null;
    return !region || this.hasTimeRange(region);
  }

  // Trying to play a segment of zero duration stops playback and moves the cursor to the segment
  private stopAtRegion(region: Region): void {
    this.ws?.pause();
    this.ws?.setTime(region.start);
  }

  private applyRegionHighlight(): void {
    for (const region of this.regionsPlugin?.getRegions() ?? []) {
      if (region.id === this.DRAFT_REGION_ID) continue;
      region.element?.style.setProperty(
        '--region-opacity',
        region.id === this.highlightedRegionId ? '40%' : '20%',
        'important');
    }
  }

  private getRegion(regionId: string): Region | null {
    const regions = this.regionsPlugin?.getRegions();
    return regions?.find(r => r.id === regionId) ?? null;
  }

  private setRegions(regions: RegionParams[]): void {
    this.regionsPlugin.getRegions()
      .filter(r => r.id !== this.DRAFT_REGION_ID)
      .forEach(r => r.remove());

    for (const region of regions) {
      this.regionsPlugin.addRegion({
        ...region,
        drag: false,
        resize: false,
      });
    }

    const pending = this.pendingRegionId;
    if (pending) {
      this.pendingRegionId = null;
      this.goToRegion(pending);
    }

    this.applyRegionHighlight();
  }

  private clearWaveform(): void {
    this.cursorRegionId.set(null);
    this.regionsPlugin?.clearRegions();
  }

  private clearDraftRegion(): void {
    this.syncingDraft = true;
    try {
      this.getRegion(this.DRAFT_REGION_ID)?.remove();
    } finally {
      this.syncingDraft = false;
    }
  }

  private setDraftRegion(params: RegionParams): void {
    this.syncingDraft = true;
    try {
      const options: RegionParams = {
        id: this.DRAFT_REGION_ID,
        start: params.start,
        end: params.end,
        content: params.content,
        drag: true,
        resize: true,
      };

      const existing = this.getRegion(this.DRAFT_REGION_ID);
      if (existing) {
        existing.setOptions(options);
      } else {
        this.regionsPlugin?.addRegion(options);
      }
    } finally {
      this.syncingDraft = false;
    }
  }

  private sliderToMinPxPerSec(level: number): number {
    const duration = this.ws?.getDuration() ?? 0;
    const width = this.waveformEl?.nativeElement?.clientWidth ?? 0;
    if (!duration || !width || level <= 0) return 0; // fit width

    const fit = width / duration;
    const max = Math.max(this.maxZoom, fit + 1);
    const ratio = Math.min(1, Math.max(0, level / this.maxZoom));
    return fit + ratio * (max - fit);
  }

  private minPxPerSecToSlider(minPxPerSec: number): number {
    const duration = this.ws?.getDuration() ?? 0;
    const width = this.waveformEl?.nativeElement?.clientWidth ?? 0;
    if (!duration || !width || minPxPerSec <= 0) return 0;

    const fit = width / duration;
    const max = Math.max(this.maxZoom, fit + 1);
    if (minPxPerSec <= fit) return 0;

    return Math.round(((minPxPerSec - fit) / (max - fit)) * this.maxZoom);
  }

  private setZoom(level: number): void {
    const clamped = Math.max(0, Math.min(this.maxZoom, level));
    this.zoomLevel.set(clamped);
    this.ws?.zoom(this.sliderToMinPxPerSec(clamped));
  }

  private getSkipDuration(): number {
    if (!this.ws) return 1;
    const zoom = this.zoomLevel();
    if (zoom > 0) {
      const containerWidth = this.waveformEl.nativeElement.clientWidth;
      const visibleDuration = containerWidth / zoom;
      return Math.max(0.1, Math.min(10, visibleDuration * 0.02));
    }
    return Math.max(0.5, Math.min(10, this.ws.getDuration() * 0.02));
  }

  private getMarkerStarts(includeDraft = false): number[] {
    return this.regions()
      .filter(r => includeDraft || r.id !== this.DRAFT_REGION_ID)
      .map(r => r.start ?? 0)
      .filter((t): t is number => Number.isFinite(t))
      .sort((a, b) => a - b);
  }

  private findPrevMarker(markers: number[], now: number, eps = 0.01): number | null {
    for (let i = markers.length - 1; i >= 0; i--) {
      if (markers[i] < now - eps) return markers[i];
    }
    return null;
  }

  private findNextMarker(markers: number[], now: number, eps = 0.01): number | null {
    for (let i = 0; i < markers.length; i++) {
      if (markers[i] > now + eps) return markers[i];
    }
    return null;
  }

  private hasPrevMarker(now: number, markers: number[], eps = 0.01): boolean {
    return markers.some(t => t < now - eps);
  }

  private hasNextMarker(now: number, markers: number[], eps = 0.01): boolean {
    return markers.some(t => t > now + eps);
  }
}
